import React from 'react';
import { useForm, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/AuthContext';
import {
    useCollaboratorIdByEmailQuery,
    useCollaboratorSelfQuery,
    useUpdateCollaboratorSelfMutation,
    type CollaboratorSelf
} from '@/features/Collaborators/Data';
import { createContextualCollaboratorSchema, PROFILE_UPDATE_STEP_FIELDSETS, collaboratorSchemaHelpers } from '@/models';
import { LoadingContainer } from '@/components/shared/LoadingContainer';
import { ProfileUpdateForm } from '../../Contexts/ProfileUpdate/ProfileUpdateForm';
import { useToast } from '@/hooks/useToast';
import { normalizeCollaboratorFormData } from '@/utils/form-validation/formDataNormalizer';
import { useCanUpdateProfileQuery } from '@/hooks/useUpdateWindows';
import { CalendarDaysIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const ProfileUpdatePage: React.FC = () => {
    const { user, refreshUser } = useAuth();
    const { success, error, warning } = useToast();

    // Query para obter ID do colaborador por email
    const {
        data: collaboratorId,
        isLoading: isLoadingId,
        error: idError
    } = useCollaboratorIdByEmailQuery(user?.email || undefined);

    // Query para buscar dados self do colaborador
    const {
        data: collaboratorData,
        isLoading: isLoadingCollaborator,
        error: collaboratorError
    } = useCollaboratorSelfQuery(collaboratorId!);

    // Query para verificar se pode atualizar perfil
    const {
        data: updatePermission,
        isLoading: isLoadingPermission,
        error: permissionError
    } = useCanUpdateProfileQuery(collaboratorId || undefined);

    // Mutação para atualizar dados self
    const updateCollaboratorMutation = useUpdateCollaboratorSelfMutation();

    // 🔄 NORMALIZAR dados iniciais para evitar controlled vs uncontrolled
    const normalizedDefaultValues = React.useMemo(() => {
        return normalizeCollaboratorFormData(collaboratorData as any);
    }, [collaboratorData]);

    // STATE: step atual do wizard
    const [currentStep, setCurrentStep] = React.useState<number>(-1);

    // Sincroniza currentStep quando carregar dados do colaborador e permissões
    React.useEffect(() => {
        if (!collaboratorData || !updatePermission) return;

        const status = collaboratorData?.updatedStatus?.byUser?.status ?? 'PENDING';

        // Se não pode atualizar perfil, mostrar mensagem
        if (!updatePermission.canUpdate) {
            setCurrentStep(-2); // Estado especial para "não pode atualizar"
            return;
        }

        // Se já completou a atualização mas está dentro da janela e permite múltiplas atualizações
        if (status === 'UPDATED' && updatePermission.activeWindow?.allowMultipleUpdates) {
            setCurrentStep(-1); // Permitir nova atualização
        } else if (status === 'UPDATED') {
            setCurrentStep(7); // Já concluído e não permite múltiplas atualizações
        } else if (status === 'IN_PROGRESS') {
            const step = collaboratorData?.updatedStatus?.byUser?.currentStep ?? 0;
            setCurrentStep(step);
        } else {
            setCurrentStep(-1); // Instruction
        }
    }, [collaboratorData, updatePermission]);

    const currentFieldsets = PROFILE_UPDATE_STEP_FIELDSETS[currentStep] || [];

    // Estado para rastrear se usuário não tem conta BMG
    const [hasNoBmgAccount, setHasNoBmgAccount] = React.useState(false);

    // 🚀 FORM SETUP com dados normalizados e schema contextual
    const contextualSchema = React.useMemo(() => {
        return createContextualCollaboratorSchema("PROFILE_UPDATE", currentFieldsets, hasNoBmgAccount);
    }, [currentFieldsets, hasNoBmgAccount]);

    const form = useForm<any>({
        resolver: zodResolver(contextualSchema),
        defaultValues: normalizedDefaultValues as any,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        setValue,
        watch,
        reset,
    } = form;

    // 🔄 Reset form when new default values arrive (after Firestore load)
    React.useEffect(() => {
        if (normalizedDefaultValues && Object.keys(normalizedDefaultValues).length > 0) {
            reset(normalizedDefaultValues as any);
        }
    }, [normalizedDefaultValues, reset]);

    // Handler para submit do formulário
    const onFormSubmit = async (data: any) => {
        console.log('🎯 onFormSubmit EXECUTADO! Dados recebidos:', data);
        try {
            // Determinar próximo step e status
            const nextStep = currentStep < 0 ? 0 : currentStep + 1;
            const nextStatus: any = nextStep >= 7 ? 'UPDATED' : 'IN_PROGRESS';

            // ✅ CONVERTER dados do formulário para formato CollaboratorSelf (filtra campos permitidos + remove undefined)
            const cleanData = collaboratorSchemaHelpers.toSelfData(data);

            // Preparar dados de status
            const statusData: any = {
                status: nextStatus,
                currentStep: nextStep,
            };

            // Se completou a atualização e há janela ativa, salvar o ID da janela
            if (nextStatus === 'UPDATED' && updatePermission?.activeWindow) {
                statusData.lastUpdateWindowId = updatePermission.activeWindow.id;
                statusData.lastUpdateDate = new Date().toISOString();
            }

            const dataWithStatus = {
                ...cleanData,
                updatedStatus: {
                    byUser: statusData,
                },
            };

            console.log('🚀 Submetendo formulário:', dataWithStatus);
            await updateCollaboratorMutation.mutateAsync({
                collaboratorId: collaboratorId!,
                data: dataWithStatus as any // ✅ Cast para resolver diferenças de tipo entre schemas
            });
            success('Dados salvos!', 'Informações atualizadas com sucesso');

            // Se o usuário completou a atualização do perfil, atualizar o contexto de autenticação
            if (nextStatus === 'UPDATED') {
                await refreshUser();
            }

            // Avança para o próximo step
            setCurrentStep(prev => {
                const next = nextStep;

                // Atualizar valor do form para persistir depois
                setValue('updatedStatus.byUser.currentStep' as any, next as any, { shouldValidate: false });
                setValue('updatedStatus.byUser.status', nextStatus, { shouldValidate: false });

                return next;
            });
        } catch (submitError) {
            console.error('💥 ERRO NO SUBMIT:', submitError);
            const errorMessage = submitError instanceof Error ? submitError.message : 'Erro desconhecido';
            error('Erro ao salvar dados', errorMessage);
            throw submitError;
        }
    };

    // Callback de erro após validação
    const onInvalid = (invalidErrors: FieldErrors<any>) => {
        console.group('❌ onInvalid – erros de validação');
        console.log(invalidErrors);
        console.groupEnd();

        const errorCount = Object.keys(invalidErrors).length;
        warning(`Você tem ${errorCount} campo(s) obrigatório(s) não preenchido(s).`);
    };

    // Handler que será passado para o componente filho
    const handleFormSubmit = async () => {
        handleSubmit(onFormSubmit, onInvalid)();
    };

    // Handler para cancelar (não implementado neste contexto)
    const handleCancel = () => {
        // Implementar lógica de cancelamento se necessário
        console.log('Cancelamento não implementado');
    };

    // Handler para voltar para o step anterior
    const handlePrevStep = () => {
        setCurrentStep(prev => {
            const previousStep = Math.max(prev - 1, -1);

            // Sincronizar com valores no formulário (não validação)
            setValue('updatedStatus.byUser.currentStep' as any, previousStep as any, { shouldValidate: false });

            return previousStep;
        });
    };

    // Handler para mudança do checkbox "não tem conta BMG"
    const handleNoBmgAccountChange = (hasNoBmgAccount: boolean) => {
        setHasNoBmgAccount(hasNoBmgAccount);
    };

    // Estados de erro
    if (idError || collaboratorError || permissionError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h1 className="text-xl font-semibold text-red-600 mb-4">
                        Erro ao carregar dados
                    </h1>
                    <p className="text-gray-600">
                        {idError?.message || collaboratorError?.message || permissionError?.message}
                    </p>
                </div>
            </div>
        );
    }

    // Estados de loading
    if (isLoadingId || isLoadingCollaborator || isLoadingPermission || !collaboratorId || !collaboratorData || !updatePermission) {
        return <LoadingContainer message="Carregando dados do perfil..." />;
    }

    const localUpdateStatus = watch('updatedStatus');

    // Se não pode atualizar perfil, mostrar mensagem informativa
    if (currentStep === -2) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="max-w-md mx-auto text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                        <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                    </div>
                    <h1 className="mt-4 text-xl font-semibold text-gray-900">
                        Atualização não permitida
                    </h1>
                    <p className="mt-2 text-gray-600">
                        {updatePermission.reason || 'Não há janela de atualização ativa no momento'}
                    </p>
                    {updatePermission.activeWindow && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-md">
                            <div className="flex items-center">
                                <CalendarDaysIcon className="h-5 w-5 text-blue-400 mr-2" />
                                <div className="text-sm text-blue-700">
                                    <p className="font-medium">{updatePermission.activeWindow.title}</p>
                                    {updatePermission.activeWindow.description && (
                                        <p className="mt-1">{updatePermission.activeWindow.description}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Renderização principal
    return (
        <ProfileUpdateForm
            collaboratorId={collaboratorData?.id || collaboratorId || ''}
            initialData={collaboratorData as any}
            currentStep={currentStep}
            updateStatus={localUpdateStatus}
            control={control}
            errors={errors}
            setValue={setValue}
            watch={watch}
            isSubmitting={isSubmitting}
            onSubmit={handleFormSubmit}
            onCancel={handleCancel}
            onPrevStep={handlePrevStep}
            onNoBmgAccountChange={handleNoBmgAccountChange}
        />
    );
};

export default ProfileUpdatePage; 